import { Tag, Package, Box, Layers, Trash2, ShieldOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useLineItemExclusions } from '../hooks/useLineItemExclusions';
import { formatRuleLabel } from '../lib/format';
import type { ExclusionRuleRow, MatchField } from '@threadheads/shared';

interface Props {
  rules: ExclusionRuleRow[];
  isLoading: boolean;
  /** null = org-wide, else the location the table is for — used for mutation scoping */
  locationId: string | null;
  emptyState: {
    title: string;
    description: string;
  };
  /** IDs that should flash a ring after being newly added */
  highlightIds?: string[];
}

export function ExclusionRulesTable({
  rules,
  isLoading,
  locationId,
  emptyState,
  highlightIds,
}: Props) {
  const { t } = useTranslation('settings');
  const { toggleActive, togglePending, togglingRuleId, deleteRule, deletePending } =
    useLineItemExclusions(locationId);

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (rules.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-8 text-center">
        <ShieldOff className="mx-auto h-8 w-8 text-muted-foreground" />
        <h3 className="mt-3 text-sm font-medium">{emptyState.title}</h3>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
          {emptyState.description}
        </p>
      </div>
    );
  }

  const highlighted = new Set(highlightIds ?? []);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[48px]" />
          <TableHead>{t('exclusions.table.matchColumn')}</TableHead>
          <TableHead className="w-[100px]">{t('exclusions.table.activeColumn')}</TableHead>
          <TableHead className="w-[60px]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rules.map((rule) => {
          const field = rule.match_field;
          const { primary, secondary } = formatRuleLabel({
            matchField: field,
            value: rule.value,
            displayLabel: rule.display_label,
          });
          const isHighlighted = highlighted.has(rule.id);
          const thisRowToggling = togglePending && togglingRuleId === rule.id;
          return (
            <TableRow
              key={rule.id}
              data-just-added={isHighlighted ? '' : undefined}
              className={
                isHighlighted
                  ? 'bg-primary/5 transition-colors duration-500'
                  : undefined
              }
            >
              <TableCell>
                <MatchFieldIcon field={field} />
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <span className={rule.is_active ? 'font-medium' : 'font-medium text-muted-foreground'}>
                      {primary}
                    </span>
                    <Badge variant="outline" className="text-[10px] font-normal uppercase">
                      {t(`exclusions.field.fieldLabel.${field}`)}
                    </Badge>
                  </div>
                  {secondary ? (
                    <span className="text-xs text-muted-foreground">{secondary}</span>
                  ) : null}
                </div>
              </TableCell>
              <TableCell>
                <Switch
                  checked={rule.is_active}
                  disabled={thisRowToggling}
                  onCheckedChange={(next) => toggleActive(rule.id, next)}
                  aria-label={
                    rule.is_active
                      ? t('exclusions.table.deactivateAria')
                      : t('exclusions.table.activateAria')
                  }
                />
              </TableCell>
              <TableCell>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={t('exclusions.table.deleteAria')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('exclusions.deleteDialog.title')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('exclusions.deleteDialog.description', { label: primary })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('exclusions.actions.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        disabled={deletePending}
                        onClick={() => deleteRule(rule.id)}
                      >
                        {t('exclusions.actions.delete')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function TableSkeleton() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[48px]" />
          <TableHead>&nbsp;</TableHead>
          <TableHead className="w-[100px]">&nbsp;</TableHead>
          <TableHead className="w-[60px]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {[0, 1, 2].map((i) => (
          <TableRow key={i} className="animate-pulse">
            <TableCell>
              <div className="h-5 w-5 rounded bg-muted" />
            </TableCell>
            <TableCell>
              <div className="h-4 w-56 rounded bg-muted" />
            </TableCell>
            <TableCell>
              <div className="h-5 w-10 rounded-full bg-muted" />
            </TableCell>
            <TableCell>
              <div className="ml-auto h-4 w-4 rounded bg-muted" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function MatchFieldIcon({ field }: { field: MatchField }) {
  const Icon =
    field === 'shopify_product_tag'
      ? Tag
      : field === 'shopify_product_type'
        ? Package
        : field === 'shopify_product_id'
          ? Layers
          : Box;
  return <Icon className="h-4 w-4 text-muted-foreground" />;
}
